import classNames from 'classnames';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { DragType } from '../../models/dragTypes';
import MixcloudCategory, { MixcloudDiscoverSort } from '../../models/mixcloud/mixcloudCategory';
import MixcloudCloudcast, { isCloudcast } from '../../models/mixcloud/mixcloudCloudcast';
import MixcloudPlaylist from '../../models/mixcloud/mixcloudPlaylist';
import MixcloudUser from '../../models/mixcloud/mixcloudUser';
import Playlist from '../../models/playlist/playlist';
import PlaylistItem from '../../models/playlistItem/playlistItem';
import QueryIterator from '../../models/queryIterator';
import Source from '../../models/source';
import Track from '../../models/track';
import MixcloudTrack from '../../models/track/mixcloudTrack';
import { ActivePlaylistContext } from '../../services/activePlaylist/activePlaylist';
import { ColumnScope, TrackProperty } from '../../services/columnSettings/playlistColumns';
import { createAddItemsToPlaylistThenLibrary } from '../../services/library/addTracksToPlaylistThenLibrary';
import { LibraryContext } from '../../services/library/libraryService';
import useSearchBox from '../../services/shortcuts/searchBoxes';
import {
  SmallDeviceContext,
  SmallDeviceShow,
} from '../../services/smallDevices/smallDeviceService';
import * as MixcloudApi from '../../services/mixcloud/mixcloudApi';
import { Context } from '../../services/_globalContext/context';
import Constants from '../../settings';
import ErrorBoundary from '../common/errorBoundary/errorBoundary';
import {
  createTrackSortValue,
  SortValue,
} from '../common/streamingServiceCommon/searchResultSorting';
import StreamingServiceSearchResults from '../common/streamingServiceCommon/streamSvcSearchResults';
import InfoBubble from '../common/infoBubble/infoBubble';
import { mixcloudInfo } from '../common/infoBubble/infoTexts';
import useLoadMore from '../common/virtualized/useLoadMore';
import Spinner from '../common/spinner/spinner';
import LibraryPlaylists from '../library/libraryPlaylists';
import libPlsStyles from '../library/libraryPlaylists.module.scss';
import TrackPlaylistComponent from '../playlist/trackPlaylistComponent';
import s from './mixcloud.module.scss';
import ShowSearchResult, { MixcloudShowRowProps } from './searchResults/showSearchResult';
import UserSearchResult, { MixcloudUserRowProps } from './searchResults/userSearchResult';

/**
 * Sorting for the two result lists. At module scope so the per-item cache
 * inside survives re-renders - see searchResultSorting.ts.
 */
const showSortValue = createTrackSortValue<MixcloudCloudcast>(MixcloudTrack.from);

/** An account has a name and nothing else a column could order it by. */
const userSortValue: SortValue<MixcloudUser> = (user, prop) =>
  prop === TrackProperty.title ? user.name : undefined;

enum MixcloudSearchType {
  Show = 'show',
  User = 'user',
}

/** Which of an account's two public lists is showing. */
enum UserListKind {
  Uploads = 'cloudcasts',
  Favourites = 'favorites',
}

/**
 * What the middle list is showing: an account's uploads, an account's
 * favourites, or a category.
 *
 * `key` identifies the list rather than the thing it came from - an account's
 * uploads and its favourites are two lists - because it is what the playlist
 * is keyed by.
 */
interface ShowListSource {
  key: string;
  name: string;
  /** Set when the list came from an account, for the Uploads/Favourites switch. */
  user?: MixcloudUser;
  list?: UserListKind;
  /** Set when the list came from browsing, for the popular/latest switch. */
  category?: MixcloudCategory;
  /** Set when the list is one of yours, for the highlight in the column. */
  mine?: boolean;
  /** Set when the list is one of your Mixcloud playlists. */
  playlist?: MixcloudPlaylist;
}

interface Props {}

/**
 * The Mixcloud page: search for shows or for the people who upload them,
 * open an account to see everything it has posted, paste a mixcloud.com
 * link, and from any of those add to the library and to playlists, or play.
 *
 * The SoundCloud page with both halves that needed a token taken out. There
 * is no sign-in button and no "your likes", because the open API has no
 * concept of who is asking (docs/MIXCLOUD.md section 3) - so the page opens
 * straight onto search, and the right-hand column that holds a user's own
 * playlists elsewhere in Fadila is simply absent.
 */
export default React.memo(function MixcloudComponent({}: Props) {
  const ctx = useContext(Context);
  const queryRef = useSearchBox('search');
  const activePlaylist = useContext(ActivePlaylistContext);
  const libCtx = useContext(LibraryContext);
  const smallDevSvc = useContext(SmallDeviceContext);

  // Search State
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchType, setSearchType] = useState<MixcloudSearchType>();

  const [searchShowsIterator, setSearchShowsIterator] =
    useState<QueryIterator<MixcloudCloudcast>>();
  const [searchShowsResults, setSearchShowsResults] = useState<MixcloudCloudcast[]>([]);
  const [searchShowsLoadingMore, searchShowsLoadMore] = useLoadMore({
    items: searchShowsResults,
    setItems: setSearchShowsResults,
    queryIterator: searchShowsIterator,
    setIterator: setSearchShowsIterator,
  });

  const [searchUsersIterator, setSearchUsersIterator] = useState<QueryIterator<MixcloudUser>>();
  const [searchUsersResults, setSearchUsersResults] = useState<MixcloudUser[]>([]);
  const [searchUsersLoadingMore, searchUsersLoadMore] = useLoadMore({
    items: searchUsersResults,
    setItems: setSearchUsersResults,
    queryIterator: searchUsersIterator,
    setIterator: setSearchUsersIterator,
  });
  // Search State End

  // Show list State - one account's uploads
  const [listSource, setListSource] = useState<ShowListSource>();
  const [listIterator, setListIterator] = useState<QueryIterator<MixcloudCloudcast>>();
  const [listTracks, setListTracks] = useState<Track[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listLoadingMore, listLoadMore] = useLoadMore<Track, MixcloudCloudcast>({
    items: listTracks,
    setItems: setListTracks,
    queryIterator: listIterator,
    setIterator: setListIterator,
    itemMapper: MixcloudTrack.from,
  });
  // Show list State End

  const listPlaylistItems = useMemo(() => listTracks.map(PlaylistItem.from), [listTracks]);
  const playlist = useMemo(
    () =>
      listSource
        ? new Playlist(listSource.name, listPlaylistItems, listSource.key)
        : new Playlist('Null', [], 'Null'),
    // A new source always comes with a new tracks array (openShowList sets
    // both), so watching the tracks is enough - and watching the source too
    // would build a playlist that briefly pairs the new name with the old
    // shows. Same reasoning as the SoundCloud page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listPlaylistItems],
  );

  /**
   * There is no service wrapper between the page and the API for this
   * source - nothing to refresh, nothing to sign in - so reporting is the
   * page's own job. MixcloudApiError carries Mixcloud's own sentence.
   */
  const report = useCallback(
    function report(err: any) {
      console.debug(err);
      ctx.callSnackbar(err);
    },
    [ctx],
  );

  const clearSearch = useCallback(function clearSearch() {
    setSearchType(undefined);
    setSearchShowsIterator(undefined);
    setSearchShowsResults([]);
    setSearchUsersIterator(undefined);
    setSearchUsersResults([]);
  }, []);

  const clearShowList = useCallback(function clearShowList() {
    setListSource(undefined);
    setListIterator(undefined);
    setListTracks([]);
  }, []);

  const openShowList = useCallback(
    async function openShowList(
      source: ShowListSource,
      fetchFirstPage: () => Promise<QueryIterator<MixcloudCloudcast>>,
    ) {
      clearSearch();
      setListIterator(undefined);
      setListLoading(true);

      try {
        const iterator = await fetchFirstPage();

        setListSource(source);
        setListTracks(iterator.results.map(MixcloudTrack.from));
        setListIterator(iterator);
      } catch (err) {
        report(err);
      } finally {
        setListLoading(false);
      }
    },
    [clearSearch, report],
  );

  const openUser = useCallback(
    function openUser(user: MixcloudUser, list: UserListKind = UserListKind.Uploads) {
      const favourites = list === UserListKind.Favourites;

      return openShowList(
        {
          key: user.key + list,
          name: favourites ? `${user.name} - favourites` : user.name,
          user,
          list,
        },
        () =>
          favourites
            ? MixcloudApi.getUserFavorites(user.key, Constants.ResultCount)
            : MixcloudApi.getUserCloudcasts(user.key, Constants.ResultCount),
      );
    },
    [openShowList],
  );

  /**
   * The categories, fetched once when the page opens. 38 of them in one
   * request with no paging, so there is nothing to load more of and no
   * reason to ask again.
   */
  const [categories, setCategories] = useState<MixcloudCategory[]>([]);

  useEffect(function loadCategories() {
    let halt = false;

    MixcloudApi.getCategories().then(
      (loaded) => {
        if (!halt) {
          setCategories(loaded);
        }
      },
      // A failure here costs the browse control and nothing else, so it goes
      // to the console rather than the snackbar - the page still searches.
      console.debug,
    );

    return function cancel() {
      halt = true;
    };
  }, []);

  const [browseSort, setBrowseSort] = useState<MixcloudDiscoverSort>('popular');

  /*
    The right-hand column: your favourites, and your Mixcloud playlists.

    "Yours" is the username in settings rather than a session, because
    Mixcloud has no sign-in to offer - its OAuth authorize page 404s. Nothing
    else is missing: playlists and favourites are both public, so this is the
    same column the signed-in services have, built from a name instead of a
    token (docs/MIXCLOUD.md 4d).
  */
  const username = ctx.settings.mixcloudUsername;
  const userKey = username ? `/${username}/` : undefined;

  const [playlists, setPlaylists] = useState<MixcloudPlaylist[]>([]);
  const [playlistsLoading, setPlaylistsLoading] = useState(false);

  useEffect(
    function loadPlaylists() {
      if (!userKey) {
        setPlaylists([]);

        return;
      }

      let halt = false;

      setPlaylistsLoading(true);

      MixcloudApi.getUserPlaylists(userKey, Constants.ResultCount)
        .then(
          (iterator) => {
            if (!halt) {
              setPlaylists(iterator.results);
            }
          },
          (err) => {
            if (!halt) {
              // A wrong username is the likely cause and the message says so,
              // so this one is worth putting in front of the user.
              report(err);
              setPlaylists([]);
            }
          },
        )
        .then(() => {
          if (!halt) {
            setPlaylistsLoading(false);
          }
        });

      return function cancel() {
        halt = true;
      };
    },
    [userKey, report],
  );

  const openFavourites = useCallback(
    function openFavourites() {
      if (!userKey) {
        return Promise.resolve();
      }

      return openShowList(
        { key: `${userKey}favorites/`, name: `${username} - favourites`, mine: true },
        () => MixcloudApi.getUserFavorites(userKey, Constants.ResultCount),
      );
    },
    [openShowList, userKey, username],
  );

  /**
   * Saving the username from this page rather than only from Settings: it
   * is where you are when you notice the column is empty.
   */
  const handleUsernameSubmit = useCallback(
    function handleUsernameSubmit(ev: React.FormEvent<HTMLFormElement>) {
      ev.preventDefault();

      const form = ev.currentTarget;
      const input = form.elements.namedItem('mixcloud-username-page') as HTMLInputElement;

      ctx.setMixcloudUsername(input.value);
    },
    [ctx],
  );

  const openPlaylist = useCallback(
    function openPlaylist(pl: MixcloudPlaylist) {
      return openShowList({ key: pl.key, name: pl.name, playlist: pl, mine: true }, () =>
        MixcloudApi.getPlaylistShows(pl.key, Constants.ResultCount),
      );
    },
    [openShowList],
  );

  const openCategory = useCallback(
    function openCategory(category: MixcloudCategory, sort: MixcloudDiscoverSort) {
      return openShowList(
        {
          key: category.key + sort,
          name: `${category.name} - ${sort}`,
          category,
        },
        () => MixcloudApi.getCategoryShows(category.slug, sort, Constants.ResultCount),
      );
    },
    [openShowList],
  );

  const handleBrowse = useCallback(
    function handleBrowse(ev: React.ChangeEvent<HTMLSelectElement>) {
      const category = categories.find((c) => c.slug === ev.target.value);

      if (category) {
        openCategory(category, browseSort);
      }
    },
    [categories, browseSort, openCategory],
  );

  const handleBrowseSort = useCallback(
    function handleBrowseSort(ev: React.ChangeEvent<HTMLSelectElement>) {
      const sort = ev.target.value as MixcloudDiscoverSort;

      setBrowseSort(sort);

      // Re-open what is already showing in the other order, rather than making
      // the user pick the category again.
      if (listSource && listSource.category) {
        openCategory(listSource.category, sort);
      }
    },
    [listSource, openCategory],
  );

  const handleSearch = useCallback(
    async function handleSearch(ev: React.FormEvent<HTMLFormElement>) {
      ev.preventDefault();

      const form = ev.currentTarget;
      const query = (form.elements.namedItem('query') as HTMLInputElement).value.trim();
      const type = (form.elements.namedItem('type') as HTMLInputElement)
        .value as MixcloudSearchType;

      if (!query) {
        return;
      }

      if (MixcloudApi.isMixcloudUrl(query)) {
        // A pasted link. Mixcloud needs no resolve endpoint - the path of the
        // page is the key - so this is one request for whatever it names.
        clearShowList();
        clearSearch();
        setSearchLoading(true);

        try {
          const resource = await MixcloudApi.resolve(query);

          if (isCloudcast(resource)) {
            setSearchType(MixcloudSearchType.Show);
            setSearchShowsResults([resource]);
            setSearchShowsIterator({ results: [resource] });
          } else {
            await openUser(resource as MixcloudUser);
          }
        } catch (err) {
          report(err);
        } finally {
          setSearchLoading(false);
        }

        return;
      }

      clearShowList();
      setSearchShowsIterator(undefined);
      setSearchUsersIterator(undefined);
      setSearchType(type);
      setSearchLoading(true);

      try {
        if (type === MixcloudSearchType.User) {
          const res = await MixcloudApi.searchUsers(query, Constants.ResultCount);

          setSearchUsersResults(res.results);
          setSearchUsersIterator(res);
        } else {
          const res = await MixcloudApi.searchCloudcasts(query, Constants.ResultCount);

          setSearchShowsResults(res.results);
          setSearchShowsIterator(res);
        }
      } catch (err) {
        report(err);
      } finally {
        setSearchLoading(false);
      }
    },
    [clearSearch, clearShowList, openUser, report],
  );

  const addItemsToPlaylistThenLibrary = useMemo(
    () => createAddItemsToPlaylistThenLibrary<MixcloudCloudcast>(ctx, libCtx, MixcloudTrack.from),
    [ctx, libCtx],
  );

  /**
   * For playing single shows from search results.
   * In those cases we do not add to library.
   */
  const playSearchResult = useCallback(
    function playSearchResult(item: MixcloudCloudcast) {
      const plItm = new PlaylistItem(item.key, Source.Mixcloud);

      const doPlay = true;
      activePlaylist.setActivePlaylist([plItm], 0, doPlay);
    },
    [activePlaylist],
  );

  const showSearchProps: Partial<MixcloudShowRowProps> = useMemo(
    () => ({
      handlePlayShow: playSearchResult,
      addItemsThenToLibrary: addItemsToPlaylistThenLibrary,
    }),
    [playSearchResult, addItemsToPlaylistThenLibrary],
  );

  const userSearchProps: Partial<MixcloudUserRowProps> = useMemo(
    () => ({
      openUser,
    }),
    [openUser],
  );

  return (
    <ErrorBoundary>
      <article className="grid-container full fluid">
        <div className="grid-x">
          <div className="cell shrink">
            <article className={`mainViewport ${ctx.playerSize}`}>
              <LibraryPlaylists />
            </article>
          </div>

          <div className="cell auto">
            <div className={`mainViewport ${ctx.playerSize}`}>
              <section>
                <form onSubmit={handleSearch}>
                  <div className={`grid-x ${s.searchBar}`}>
                    <InfoBubble label={mixcloudInfo.label}>{mixcloudInfo.text}</InfoBubble>
                    <label className={s.searchLabel} htmlFor="query">
                      <div className="grid-x grid-margin-x">
                        <input
                          className="cell auto"
                          type="text"
                          name="query"
                          ref={queryRef}
                          id="query"
                          placeholder="Search Mixcloud, or paste a mixcloud.com link"
                        />
                        <div className={classNames('cell', 'shrink', s.searchTypeControls)}>
                          <label htmlFor={MixcloudSearchType.Show}>
                            Shows
                            <input
                              type="radio"
                              id={MixcloudSearchType.Show}
                              name="type"
                              value={MixcloudSearchType.Show}
                              defaultChecked
                            />
                          </label>
                          <label htmlFor={MixcloudSearchType.User}>
                            People
                            <input
                              type="radio"
                              id={MixcloudSearchType.User}
                              name="type"
                              value={MixcloudSearchType.User}
                            />
                          </label>
                        </div>
                        <button className="cell shrink button">Search</button>
                      </div>
                    </label>
                  </div>
                </form>

                {/*
                  Browse, for when you do not already know what you are
                  looking for. Mixcloud's own discovery endpoints at the root
                  are gone - /popular/ is read as a user called "popular" -
                  and this is what replaced them: 38 categories, each with a
                  popular and a latest list.
                */}
                {categories.length > 0 && (
                  <div className={s.browseBar}>
                    <label className={s.browseLabel} htmlFor="mixcloud-category">
                      Browse
                    </label>
                    <select
                      id="mixcloud-category"
                      className={s.browseSelect}
                      value={listSource && listSource.category ? listSource.category.slug : ''}
                      onChange={handleBrowse}
                    >
                      <option value="">Pick a category</option>
                      {/*
                        Mixcloud mixes music genres and spoken-word categories
                        in one list; grouped, because "Comedy" between "Chillout"
                        and "Deep House" reads as a mistake.
                      */}
                      <optgroup label="Music">
                        {categories
                          .filter((c) => c.format === 'music')
                          .map((category) => (
                            <option key={category.key} value={category.slug}>
                              {category.name}
                            </option>
                          ))}
                      </optgroup>
                      <optgroup label="Talk">
                        {categories
                          .filter((c) => c.format !== 'music')
                          .map((category) => (
                            <option key={category.key} value={category.slug}>
                              {category.name}
                            </option>
                          ))}
                      </optgroup>
                    </select>

                    <select
                      className={s.browseSelect}
                      value={browseSort}
                      onChange={handleBrowseSort}
                      aria-label="Order"
                    >
                      <option value="popular">Popular</option>
                      <option value="latest">Latest</option>
                    </select>
                  </div>
                )}
              </section>

              {searchType === undefined && listSource && listSource.user && (
                // An account has two public lists and no sign-in is needed
                // for either - see docs/MIXCLOUD.md section 3.
                <div className={s.browseBar}>
                  <span className={s.browseLabel}>{listSource.user.name}</span>
                  <button
                    className={classNames('button', {
                      hollow: listSource.list !== UserListKind.Uploads,
                    })}
                    onClick={() => openUser(listSource.user!, UserListKind.Uploads)}
                  >
                    Uploads
                  </button>
                  <button
                    className={classNames('button', {
                      hollow: listSource.list !== UserListKind.Favourites,
                    })}
                    onClick={() => openUser(listSource.user!, UserListKind.Favourites)}
                  >
                    Favourites
                  </button>
                </div>
              )}

              {searchType === undefined && (
                <div className={`items-list ${s.mainViewContainer}`}>
                  {(listLoading || listIterator !== undefined) && listSource && (
                    <TrackPlaylistComponent
                      moveable={false}
                      dragType={DragType.TrackItems}

                      playlist={playlist}
                      trackItems={listTracks}
                      loading={listLoading}

                      display={listIterator !== undefined}
                      isComplete={() => !listIterator?.next}
                      // Mixcloud pages carry no total; TrackPlaylist sizes
                      // itself by what has loaded when this is undefined.
                      totalItemCount={() => listIterator?.totalResults!}

                      columnScope={ColumnScope.Mixcloud}

                      loadMore={listLoadMore}
                      hasMore={listIterator?.next !== undefined}
                      isNextPageLoading={listLoadingMore}
                    />
                  )}
                </div>
              )}

              {searchType === MixcloudSearchType.Show && (
                <div className={`items-list ${s.mainViewContainer}`}>
                  {(searchLoading || searchShowsIterator !== undefined) && (
                    <StreamingServiceSearchResults<MixcloudCloudcast, MixcloudShowRowProps>
                      columnScope={ColumnScope.MixcloudShowSearch}
                      itemComponent={ShowSearchResult}
                      results={searchShowsResults}
                      loading={searchLoading}
                      display={searchShowsIterator !== undefined}
                      isComplete={() => !searchShowsIterator?.next}

                      totalItemCount={() => searchShowsIterator?.totalResults!}
                      totalItemCountString={false}

                      isNextPageLoading={searchShowsLoadingMore}
                      loadMore={searchShowsLoadMore}
                      hasMore={searchShowsIterator?.next !== undefined}

                      itemSize={26}
                      sortValue={showSortValue}
                      props={showSearchProps}
                    />
                  )}
                </div>
              )}

              {searchType === MixcloudSearchType.User && (
                <div className={`items-list ${s.mainViewContainer}`}>
                  {(searchLoading || searchUsersIterator !== undefined) && (
                    <StreamingServiceSearchResults<MixcloudUser, MixcloudUserRowProps>
                      columnScope={ColumnScope.MixcloudUserSearch}
                      itemComponent={UserSearchResult}
                      results={searchUsersResults}
                      loading={searchLoading}
                      display={searchUsersIterator !== undefined}
                      isComplete={() => !searchUsersIterator?.next}

                      totalItemCount={() => searchUsersIterator?.totalResults!}
                      totalItemCountString={false}

                      isNextPageLoading={searchUsersLoadingMore}
                      loadMore={searchUsersLoadMore}
                      hasMore={searchUsersIterator?.next !== undefined}

                      sortValue={userSortValue}
                      props={userSearchProps}
                    />
                  )}
                </div>
              )}
            </div>
          </div>

          {/*
            Yours, on the right, where every other service keeps its
            playlists - built from a username rather than a session, because
            Mixcloud has no sign-in to offer and needs none: playlists and
            favourites are both public (docs/MIXCLOUD.md 4e).
          */}
          <div className="cell shrink">
            <div className={`mainViewport ${ctx.playerSize}`}>
              <article
                className={classNames(libPlsStyles.playlistList, s.mixcloudPlaylists, {
                  [libPlsStyles.open]: smallDevSvc.state === SmallDeviceShow.StreamingPlaylists,
                })}
              >
                {userKey ? (
                  <>
                    <button
                      className={classNames('button', s.myListButton, {
                        hollow: !(listSource && listSource.mine && !listSource.playlist),
                      })}
                      onClick={openFavourites}
                    >
                      Favourites
                    </button>

                    {playlistsLoading ? (
                      <Spinner />
                    ) : (
                      playlists.map((pl) => (
                        <button
                          key={pl.key}
                          className={classNames('button', s.myListButton, {
                            hollow: !(
                              listSource &&
                              listSource.playlist &&
                              listSource.playlist.key === pl.key
                            ),
                          })}
                          onClick={() => openPlaylist(pl)}
                          title={
                            pl.cloudcast_count !== undefined
                              ? `${pl.name} - ${pl.cloudcast_count} shows`
                              : pl.name
                          }
                        >
                          {pl.name}
                        </button>
                      ))
                    )}
                  </>
                ) : (
                  <form className={s.usernameForm} onSubmit={handleUsernameSubmit}>
                    <label className={s.usernameLabel} htmlFor="mixcloud-username-page">
                      Your Mixcloud
                    </label>
                    <input
                      id="mixcloud-username-page"
                      name="mixcloud-username-page"
                      type="text"
                      placeholder="username"
                      aria-label="Your Mixcloud username"
                    />
                    <button className="button secondary">Save</button>
                    <p className={s.usernameHelp}>
                      For your favourites and playlists. A username rather than a login because
                      Mixcloud's sign-in page is gone - nothing else is missing, both lists are
                      public.
                    </p>
                  </form>
                )}
              </article>
            </div>
          </div>
        </div>
      </article>
    </ErrorBoundary>
  );
});
